import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import type { ColumnDef, SortingState, TableMeta } from "@tanstack/react-table";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ChevronLeft, ChevronRight, ArrowUp } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export interface DataTableProps<TData extends { id?: string; estimateId?: string }> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TData, any>[];
  data: TData[];
  isLoading: boolean;
  pageSize: number;
  onClickRow?: (id: string) => void;
  meta?: TableMeta<TData>;
  renderSkeleton?: (columnId: string) => ReactNode;
  emptyState?: ReactNode;
  manualPagination?: boolean;
  pageCount?: number;
  totalRows?: number;
  pageIndex?: number;
  onPageChange?: (pageIndex: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  /** Nested inside a bordered card: drop outer rounding so the card edge is the frame. */
  embedded?: boolean;
  /**
   * Use full container width with flexible columns (no fixed 150px cells).
   * Set `meta.columnClassName` on columns for `table-fixed` width hints (e.g. `w-[20%] min-w-0`).
   */
  fluidLayout?: boolean;
}

export function DataTable<TData extends { id?: string; estimateId?: string }>({
  columns,
  data,
  isLoading,
  pageSize,
  onClickRow,
  meta,
  renderSkeleton,
  emptyState,
  manualPagination = false,
  pageCount,
  totalRows: totalRowsProp,
  pageIndex,
  onPageChange,
  onPageSizeChange,
  embedded = false,
  fluidLayout = false,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      ...(manualPagination && pageIndex !== undefined ? { pagination: { pageIndex, pageSize } } : {}),
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(!manualPagination ? { getPaginationRowModel: getPaginationRowModel() } : {}),
    getFilteredRowModel: getFilteredRowModel(),
    meta,
    ...(manualPagination
      ? {
          manualPagination: true,
          pageCount: pageCount ?? -1,
        }
      : {
          initialState: {
            pagination: { pageSize },
          },
        }),
  });

  useEffect(() => {
    if (!manualPagination) {
      table.setPageSize(pageSize);
    }
  }, [manualPagination, pageSize, table]);

  const isManual = manualPagination && pageIndex !== undefined;

  const currentPageIndex = isManual ? pageIndex : table.getState().pagination.pageIndex;
  const currentPageSize = isManual ? pageSize : table.getState().pagination.pageSize;

  // Use the real total from the server when available, otherwise use client-side count
  const displayTotal = isManual && totalRowsProp !== undefined ? totalRowsProp : table.getFilteredRowModel().rows.length;

  const rangeStart = displayTotal === 0 ? 0 : currentPageIndex * currentPageSize + 1;
  const rangeEnd = isManual
    ? currentPageIndex * currentPageSize + data.length
    : Math.min((currentPageIndex + 1) * currentPageSize, displayTotal);

  const canPreviousPage = isManual ? currentPageIndex > 0 : table.getCanPreviousPage();
  const canNextPage = isManual ? pageCount !== undefined && currentPageIndex < pageCount - 1 : table.getCanNextPage();

  const handlePreviousPage = () => {
    if (isManual && onPageChange) {
      onPageChange(currentPageIndex - 1);
    } else {
      table.previousPage();
    }
  };

  const handleNextPage = () => {
    if (isManual && onPageChange) {
      onPageChange(currentPageIndex + 1);
    } else {
      table.nextPage();
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    if (isManual && onPageSizeChange) {
      onPageSizeChange(newSize);
    } else {
      table.setPageSize(newSize);
      // Also notify the parent so it can keep its `pageSize` prop in sync (e.g. for skeleton row
      // counts on next load). Subsequent prop-driven `useEffect` to `setPageSize` is a no-op.
      onPageSizeChange?.(newSize);
    }
  };

  const isEmpty = !isLoading && data.length === 0;

  const headerCellClass = cn(
    "truncate text-left text-xs font-medium text-[#71717A]",
    fluidLayout ? "px-4 !py-2" : "px-6 !py-2",
  );

  const bodyCellClass = (columnClassName?: string, bodyClassName?: string) =>
    cn(
      "truncate align-middle text-sm",
      fluidLayout ? "min-w-0 px-4 !py-2" : "max-w-[150px] min-w-[150px] px-6 !py-2",
      columnClassName,
      bodyClassName,
    );

  return (
    <div
      className={`flex h-full w-full flex-col justify-between bg-white ${embedded ? "rounded-none" : "rounded-lg"}`}
    >
      <div className={cn(isEmpty ? "h-full" : "", "min-w-0 w-full", fluidLayout ? "overflow-x-hidden" : "overflow-x-auto")}>
        <Table className={cn("w-full border-collapse", fluidLayout && "table-fixed")}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="border-collapse border-[0.5px] border-[#eeeff1] bg-gray-100">
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sortDir = header.column.getIsSorted();
                  const columnMeta = header.column.columnDef.meta as { columnClassName?: string } | undefined;

                  return (
                    <TableHead
                      key={header.id}
                      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                      className={cn(headerCellClass, columnMeta?.columnClassName)}
                      style={{ cursor: canSort ? "pointer" : "default" }}
                    >
                      <div className="flex w-full flex-row items-center space-x-1">
                        <div className="flex min-w-0 flex-1 items-center gap-2">{flexRender(header.column.columnDef.header, header.getContext())}</div>
                        {sortDir && <ArrowUp className={sortDir === "desc" ? "rotate-180" : ""} />}
                      </div>
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody className="!border-[0.5px] border-[#eeeff1]">
            {isLoading ? (
              Array.from({ length: pageSize }).map((_, index) => (
                <TableRow key={`skeleton-${index}`} className="border-gray-200 hover:bg-gray-50">
                  {columns.map((col, colIdx) => {
                    const skMeta = col.meta as { columnClassName?: string } | undefined;
                    return (
                      <TableCell key={colIdx} className={bodyCellClass(skMeta?.columnClassName)}>
                        {renderSkeleton?.("accessorKey" in col && typeof col.accessorKey === "string" ? col.accessorKey : `col-${colIdx}`)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : isEmpty ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="py-4 text-center text-gray-500">
                  {emptyState}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  onClick={() => {
                    if (onClickRow) onClickRow(row.original.id || row.original.estimateId || "");
                  }}
                  className="!border-collapse !border-b-1 border-[#eeeff1] hover:bg-gray-50"
                  style={{ cursor: onClickRow ? "pointer" : "default" }}
                >
                  {row.getVisibleCells().map((cell) => {
                    const columnMeta = cell.column.columnDef.meta as
                      | { bodyClassName?: string; columnClassName?: string }
                      | undefined;
                    return (
                      <TableCell
                        key={cell.id}
                        className={bodyCellClass(columnMeta?.columnClassName, columnMeta?.bodyClassName)}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {!isLoading && data.length > 0 && (
        <div className="w-full border-t border-[#eeeff1] px-4 py-2">
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-sm whitespace-nowrap">Rows per page</span>
              <Select value={String(currentPageSize)} onValueChange={(value) => handlePageSizeChange(Number(value))}>
                <SelectTrigger className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <span className="text-muted-foreground text-sm">
              {rangeStart}-{rangeEnd} of {displayTotal}
            </span>

            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-8 w-8" disabled={!canPreviousPage} onClick={handlePreviousPage}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" disabled={!canNextPage} onClick={handleNextPage}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
